import { IconCheck } from './icons.jsx';
import { url } from './url.js';

// ─────────────────────────────────────────────────────────────────────
// Den Haag-specific deep content — only rendered when stad.slug === "den-haag".
// Same pattern as RotterdamExtras: long-form SEO context + wijk-grid.
// ─────────────────────────────────────────────────────────────────────

// Geëxporteerd zodat rotterdam-wijken.jsx::StadWijkenLuxe deze data
// kan tonen in de centrale wijken-grid (single source of truth).
export const DEN_HAAG_WIJKEN = [
  { name: "Centrum & Grote Markt",         tag: "Muizen & Kakkerlakken",       desc: "Horeca en eeuwenoude panden rondom de Hofvijver zorgen voor structurele muizenoverlast." },
  { name: "Schilderswijk & Transvaal",     tag: "Ratten & Muizen",             desc: "Zwaarst getroffen wijken van Den Haag, bestrijding via VvE of woningcorporatie." },
  { name: "Laakkwartier & Spoorwijk",      tag: "Ratten",                      desc: "Rattenoverlast via riool en kanaal. Hoge bewonersomloop bemoeilijkt structurele aanpak." },
  { name: "Moerwijk & Escamp",             tag: "Ratten · Muizen · Kakkerlakken", desc: "Bewoners melden simultaan drie plagen. Situatie verergert met de jaren." },
  { name: "Scheveningen",                  tag: "Bedwantsen & Wespen",         desc: "Toeristische hotspot: hotels en Airbnb-panden kampen structureel met bedwantsen." },
  { name: "Benoordenhout & Haagse Hout",   tag: "Wespen & Mollen",             desc: "Groene residentiële wijk: wespen in spouwmuren, mollen in tuinen." },
  { name: "Statenkwartier & Zorgvliet",    tag: "Bedwantsen",                  desc: "Diplomatenwijk met expat-woningen: hoog risico op insleep via internationale reizen." },
  { name: "Segbroek & Regentessekwartier", tag: "Muizen & Wespen",             desc: "Aaneengesloten woningen: muizen via spouwmuren, wespen in het seizoen." },
  { name: "Bezuidenhout & Mariahoeve",     tag: "Muizen",                      desc: "Gemengde wijk, muizenoverlast in oudbouw neemt toe in herfst en winter." },
  { name: "Loosduinen & Wateringse Veld",  tag: "Muizen & Wespen",             desc: "Buitenwijken: muizen vanuit poldergebied, wespen in nieuwbouw-spouwmuren." },
  { name: "Ypenburg & Leidschenveen",      tag: "Muizen & Mollen",             desc: "Nieuwbouwwijken met muizen vanuit poldergebied en mollen in tuinen." },
];

function DenHaagContext({ stad }) {
  return (
    <section className="section section-bg">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 56, alignItems: 'flex-start' }} className="dhg-ctx-grid">
          <div>
            <div className="section-tag">Den Haag-context</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', fontWeight: 800, color: 'var(--navy)', margin: '10px 0 18px', lineHeight: 1.2 }}>
              Den Haag: <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>regeringsstad</em> met unieke ongedierteproblematiek
            </h2>
            <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.75, marginBottom: 16 }}>
              Den Haag is als regeringscentrum en internationale diplomatieke stad een bijzonder geval in ongediertebestrijding.
              Met meer dan 160 ambassades, tienduizenden expats, een bloeiend toerisme rondom Madurodam en Scheveningen,
              én dichtbevolkte volkswijken als de Schilderswijk en Laakkwartier, kent Den Haag een brede mix van
              ongediertetypen en -omstandigheden.
            </p>
            <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.75, marginBottom: 22 }}>
              In wijken als <strong>Transvaalkwartier, Schilderswijk en Laakkwartier</strong> is ratten- en muizenoverlast structureel.
              In <strong>Scheveningen en het Statenkwartier</strong> domineren bedwantsen door internationale gasten en reizigers.
              In het historische centrum rondom <strong>de Hofvijver en Grote Markt</strong> zorgen eeuwenoude panden voor hardnekkige
              muizenoverlast in horeca en winkels. In groene buitenwijken als <strong>Benoordenhout en Loosduinen</strong> staan
              wespen en mollen centraal.
            </p>
            <ul className="checklist">
              <li><IconCheck /> Lokale kennis van alle Haagse wijken en panden</li>
              <li><IconCheck /> Discrete service voor ambassades en overheidsgebouwen</li>
              <li><IconCheck /> Gemiddeld binnen 24 uur op locatie in Den Haag</li>
              <li><IconCheck /> Werkt met gecertificeerde partners</li>
              <li><IconCheck /> Veilig voor kinderen, huisdieren en het milieu</li>
              <li><IconCheck /> Vakkundige nacontrole na behandeling</li>
              <li><IconCheck /> Mechanische én chemische methoden beschikbaar</li>
              <li><IconCheck /> Preventiecontracten voor bedrijven (HACCP-conform)</li>
            </ul>
          </div>
          <div className="seo-photo seo-photo--filled" style={{ height: 520 }}>
            <picture>
              <source srcSet="/assets/photo-inspecteur-rotterdam.webp" type="image/webp" />
              <img
                src="/assets/photo-inspecteur-rotterdam.jpg"
                alt="ABC bestrijder in beschermende kleding tijdens ongediertebestrijding in Den Haag"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right center', display: 'block' }}
              />
            </picture>
          </div>
        </div>
        <style>{`@media (max-width:880px){ .dhg-ctx-grid{ grid-template-columns:1fr !important; } }`}</style>
      </div>
    </section>
  );
}

function DenHaagWijken() {
  return (
    <section className="section">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Per wijk in Den Haag</div>
          <h2>Ongedierteoverlast per <em>wijk</em></h2>
          <p>Den Haag kent grote contrasten per wijk, wij kennen ze allemaal.</p>
        </div>
        <div className="wijk-grid">
          {DEN_HAAG_WIJKEN.map(w => (
            <div key={w.name} className="wijk-card">
              <h3>{w.name}</h3>
              <span className="wijk-tag">{w.tag}</span>
              <p>{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DenHaagExtras({ stad }) {
  if (stad.slug !== "den-haag") return null;
  // Wijken-grid wordt nu centraal door StadWijkenLuxe (rotterdam-wijken.jsx)
  // gerenderd voor consistentie over alle 117 steden. DEN_HAAG_WIJKEN data
  // is geëxporteerd en wordt daar opgepikt.
  return <DenHaagContext stad={stad} />;
}

